import React from 'react'

import {
  Box,
  Button,
  ButtonColorVariant,
  Color,
  Display,
  FlexDirection,
  Heading2,
  JustifyContent,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { NumberPillSelector } from './NumberPillSelector/NumberPillSelector'
import {
  PriceBreakdownCard,
  type PriceBreakdownCardProps,
  type PriceRow,
} from './PriceBreakdownCard/PriceBreakdownCard'
import { PricePerPortionBanner } from './PricePerPortionBanner/PricePerPortionBanner'
import { SuitabilityGuidance } from './SuitabilityGuidance/SuitabilityGuidance'

export type BoxPriceCalculatorProps = {
  peopleOptions: number[]
  recipeOptions: number[]
  selectedPeople: number
  selectedRecipes: number
  onPeopleChange: (people: number) => void
  onRecipesChange: (recipes: number) => void
  /** Who the current people count suits, as a single line. Empty at 1. */
  suitability: string
  pricePerPortion: PriceRow
  priceBreakdown: PriceBreakdownCardProps
  ctaLabel: string
  onSelectBox: () => void
}

// Box has no margin prop, so the centring lives here rather than on a parent.
const CalculatorSurface = withCitrusTheme(Box, () => ({
  borderRadius: '8px',
  boxShadow: '0 4px 6px -2px rgba(33, 41, 48, 0.03), 0 12px 16px -4px rgba(33, 41, 48, 0.06)',
  margin: '0 auto',
}))

// Must stay paired with `width: 100%` below: the card is a flex item in the route's centred
// row, where `width: auto` sizes to content, so `maxWidth` alone caps it but never grows it.
const calculatorMaxWidth: [string, null, string, string] = ['none', null, '420px', '762px']

// Fixed rather than flexible at desktop, so the card doesn't resize as the selection changes
// the natural width of the prices. Overriding the whole `flex` shorthand is deliberate: with
// the zero basis below still in place, a separately-set `width` would be ignored.
const Column = withCitrusTheme(Box, (theme) => [
  { flex: '1 1 0', minWidth: 0 },
  theme.responsiveStyle([null, null, null, '0 0 345px'], ['flex']),
])

// A margin rather than a `gap` on the column: `gap` would also space out the suitability
// guidance below when it collapses to nothing.
const PeopleSelectorSpacing = withCitrusTheme(Box, () => ({
  marginBottom: '8px',
}))

// Only reads as a card at ≥1024, where the usage site adds the background and shadow.
const SelectorsSurface = withCitrusTheme(Box, () => ({
  borderRadius: '8px',
}))

// The banner re-parents at ≥1024. Two `display`-toggled copies rather than a breakpoint hook,
// so it stays a pure CSS switch with no hydration mismatch to flash.
const bannerShownBelowDesktop: [Display, null, null, Display] = [
  Display.Block,
  null,
  null,
  Display.None,
]
const bannerShownAtDesktop: [Display, null, null, Display] = [
  Display.None,
  null,
  null,
  Display.Block,
]

const BannerBelowDesktopSlot = withCitrusTheme(Box, () => ({
  marginTop: '16px',
}))

const BannerAtDesktopSlot = withCitrusTheme(Box, () => ({
  marginBottom: '12px',
}))

const BannerCard = withCitrusTheme(Box, () => ({
  borderRadius: '8px',
  boxShadow: '0 0 2px rgba(33, 41, 48, 0.05), 0 1px 4px rgba(33, 41, 48, 0.1)',
}))

// `minHeight` rather than `height` so the label doesn't clip at large text-zoom levels.
const Cta = withCitrusTheme(Button, () => ({
  margin: '0 auto',
  minHeight: '48px',
  borderRadius: '8px',
}))

const ctaMaxWidth: [null, null, null, string] = [null, null, null, '354px']

/**
 * Presentational only — it owns no selection state and emits no tracking. The route holds both,
 * so the deep-link preselection and the content below the widget cannot disagree with it.
 *
 * Two columns from 1024 up. The desktop design is drawn at 1280 but applies from 1024.
 */
export const BoxPriceCalculator = ({
  peopleOptions,
  recipeOptions,
  selectedPeople,
  selectedRecipes,
  onPeopleChange,
  onRecipesChange,
  suitability,
  pricePerPortion,
  priceBreakdown,
  ctaLabel,
  onSelectBox,
}: BoxPriceCalculatorProps) => {
  return (
    // Temporary: the dark hero section being built around this will own the screen-edge inset.
    // Zero at md/lg, where `calculatorMaxWidth` and the centring already provide that gap.
    <Box data-testid="boxPriceCalculator" width="100%" paddingH={[4, null, 0, 0]}>
      <CalculatorSurface
        bg={Color.White}
        width="100%"
        maxWidth={calculatorMaxWidth}
        paddingH={[3, null, 4, 6]}
        paddingV={[6, null, null, 8]}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        gap={4}
      >
        <Heading2 size={9} color={Color.ColdGrey_800}>
          How much will it cost?
        </Heading2>

        <Box
          display={Display.Flex}
          flexDirection={[FlexDirection.Column, null, null, FlexDirection.Row]}
          gap={[8, null, null, 6]}
        >
          <Column>
            <SelectorsSurface
              display={Display.Flex}
              flexDirection={FlexDirection.Column}
              justifyContent={JustifyContent.Center}
              height="100%"
              bg={[null, null, null, Color.White]}
              boxShadow={[
                null,
                null,
                null,
                '0 0 2px rgba(33, 41, 48, 0.05), 0 1px 4px rgba(33, 41, 48, 0.1)',
              ]}
              paddingV={[null, null, null, 4]}
              paddingH={[null, null, null, 4]}
            >
              <PeopleSelectorSpacing>
                <NumberPillSelector
                  legend="Select number of people"
                  options={peopleOptions}
                  value={selectedPeople}
                  onChange={onPeopleChange}
                />
              </PeopleSelectorSpacing>
              <SuitabilityGuidance suitability={suitability} />
              <NumberPillSelector
                legend="Select number of recipes"
                options={recipeOptions}
                value={selectedRecipes}
                onChange={onRecipesChange}
              />
              <BannerBelowDesktopSlot
                display={bannerShownBelowDesktop}
                data-testid="price-per-portion-mobile-slot"
              >
                <PricePerPortionBanner pricePerPortion={pricePerPortion} />
              </BannerBelowDesktopSlot>
            </SelectorsSurface>
          </Column>

          <Column>
            <BannerAtDesktopSlot
              display={bannerShownAtDesktop}
              data-testid="price-per-portion-desktop-slot"
            >
              <BannerCard bg={Color.White} paddingH={4} paddingV={3}>
                <PricePerPortionBanner pricePerPortion={pricePerPortion} />
              </BannerCard>
            </BannerAtDesktopSlot>
            <PriceBreakdownCard {...priceBreakdown} />
          </Column>
        </Box>

        <Cta
          colorVariant={ButtonColorVariant.Primary}
          width="100%"
          maxWidth={ctaMaxWidth}
          onClick={onSelectBox}
        >
          {ctaLabel}
        </Cta>
      </CalculatorSurface>
    </Box>
  )
}
